import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  TrendingUp, ArrowLeft, RotateCcw,
  DollarSign, PiggyBank, CreditCard, BarChart3, 
  AlertTriangle, CheckCircle, Target, Send, Sparkles,
  User, Phone, MapPin, Search, ChevronLeft, X
} from "lucide-react";
import HalaAvatar, { AVATAR_STATES } from "../components/HalaAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = "'Tajawal', 'Segoe UI', sans-serif";

const C = {
  primary: '#1D8F4E', primaryDark: '#167A42',
  secondary: '#1B3A6B', gold: '#C5A572',
  white: '#FFFFFF', bg: '#F8FAFC', bgGray: '#F1F5F9',
  border: '#E2E8F0', text: '#1E293B',
  muted: '#64748B', light: '#94A3B8',
  halaBubble: '#1D8F4E',
  red: '#DC2626', redBg: '#FEF2F2', redBorder: '#FECACA',
  orange: '#D97706', orangeBg: '#FFFBEB', orangeBorder: '#FDE68A',
  green: '#059669', greenBg: '#ECFDF5', greenBorder: '#A7F3D0',
};

const CAT = {
  red:    { color: C.red,    bg: C.redBg,    border: C.redBorder,    label: 'يحتاج اهتمام', icon: AlertTriangle },
  orange: { color: C.orange, bg: C.orangeBg, border: C.orangeBorder, label: 'يحتاج تحسين', icon: Target },
  green:  { color: C.green,  bg: C.greenBg,  border: C.greenBorder,  label: 'جيد', icon: CheckCircle },
};

const STEP_ICONS = { income: DollarSign, behavior: BarChart3, expenses: CreditCard, debt: AlertTriangle, savings: PiggyBank, client_info: User };

// ═══════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function TypingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
      <HalaAvatar state={AVATAR_STATES.THINKING} size={40} showStatusDot={false} />
      <div style={{ backgroundColor: C.halaBubble, padding: '12px 16px', borderRadius: 16, borderTopRightRadius: 4 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[0,150,300].map(d => <span key={d} style={{ width: 8, height: 8, backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '50%', animation: 'bounce 1s infinite', animationDelay: `${d}ms` }} />)}
        </div>
      </div>
    </div>
  );
}

function AiBubble({ text, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }} className="msg-enter">
      <HalaAvatar state={AVATAR_STATES.IDLE} size={40} showStatusDot={false} />
      <div style={{ flex: 1, maxWidth: '80%' }}>
        <div style={{ backgroundColor: C.halaBubble, padding: '12px 16px', borderRadius: 16, borderTopRightRadius: 4 }}>
          <p style={{ fontSize: 14, color: 'white', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6, fontFamily: FONT }}>{text}</p>
        </div>
        {hint && <p style={{ fontSize: 11, color: C.muted, margin: '4px 8px 0', fontFamily: FONT }}>{hint}</p>}
      </div>
    </div>
  );
}

function UserBubble({ text }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }} className="msg-enter">
      <div style={{ maxWidth: '80%' }}>
        <div style={{ backgroundColor: C.bgGray, color: C.text, padding: '12px 16px', borderRadius: 16, borderTopLeftRadius: 4, border: `1px solid ${C.border}` }}>
          <p style={{ fontSize: 14, margin: 0, lineHeight: 1.6, fontFamily: FONT }}>{text}</p>
        </div>
      </div>
    </div>
  );
}

function ScoreGauge({ score, category, size = 140 }) {
  const st = CAT[category] || CAT.orange;
  const r = size * 0.39, circ = 2 * Math.PI * r, prog = (score / 100) * circ;
  const sw = size < 120 ? 8 : 10;
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.bgGray} strokeWidth={sw} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={st.color} strokeWidth={sw}
          strokeDasharray={circ} strokeDashoffset={circ - prog} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.5s ease-out' }} />
      </svg>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
        <span style={{ fontSize: size * 0.28, fontWeight: 'bold', color: st.color, fontFamily: FONT, lineHeight: 1 }}>{score}</span>
        <span style={{ display: 'block', fontSize: size * 0.08, color: C.muted }}>من 100</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// RESULT DASHBOARD (PREMIUM TWO-COLUMN LAYOUT)
// ═══════════════════════════════════════════════════════════════════════════════

function QuickCard({ label, value, icon: Ic, accent }) {
  return (
    <div style={{ flex: '1 1 0', minWidth: 0, background: 'white', borderRadius: 12, padding: '14px 12px', border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: `${accent}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Ic style={{ width: 14, height: 14, color: accent }} />
        </div>
        <span style={{ fontSize: 11, color: C.muted, fontFamily: FONT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      </div>
      <span style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: FONT }}>{value}</span>
    </div>
  );
}

function ResultView({ result, insights, disclaimer, clientName, onRestart, onSuggestServices, onGoHome }) {
  const { total_score, category, category_label, components, components_max, summary, recommendation } = result;
  const st = CAT[category] || CAT.orange;
  const Icon = st.icon;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 860);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 860);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const disposable = summary.disposable_income;

  // ── RIGHT STICKY PANEL ──
  const StickyPanel = () => (
    <div data-testid="result-sticky-panel" style={{
      width: isMobile ? '100%' : 320, flexShrink: 0,
      position: isMobile ? 'relative' : 'sticky', top: 0, alignSelf: 'flex-start',
      display: 'flex', flexDirection: 'column', gap: 12,
      ...(isMobile ? {} : { height: 'fit-content' }),
    }}>
      {/* Score card */}
      <div style={{ background: `linear-gradient(160deg, ${st.bg}, white)`, borderRadius: 16, padding: '20px 16px', border: `1px solid ${st.border}`, textAlign: 'center' }}>
        <ScoreGauge score={total_score} category={category} size={100} />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: st.color, color: 'white', padding: '5px 14px', borderRadius: 9999, fontSize: 12, fontWeight: 700, marginTop: 10 }}>
          <Icon style={{ width: 13, height: 13 }} />{category_label}
        </div>
      </div>

      {/* Breakdown */}
      <div style={{ background: 'white', borderRadius: 14, padding: 14, border: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.secondary, fontFamily: FONT, display: 'block', marginBottom: 8 }}>التفاصيل المالية</span>
        {[
          { label: 'الدخل', value: summary.total_income, color: C.green },
          { label: 'المصروفات', value: summary.total_expenses, color: C.orange },
          { label: 'الديون', value: summary.has_debt ? summary.total_debt : 0, color: C.red },
          { label: 'الادخار', value: summary.monthly_savings, color: C.primary },
          { label: 'المتبقي', value: disposable, color: disposable >= 0 ? C.primary : C.red },
        ].map(item => (
          <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${C.bgGray}` }}>
            <span style={{ fontSize: 12, color: C.muted }}>{item.label}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: item.color }}>{item.value.toLocaleString()} ر</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <button data-testid="suggest-services-btn" onClick={onSuggestServices}
        style={{ width: '100%', padding: 12, backgroundColor: C.primary, color: 'white', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'transform 0.15s', }}>
        <TrendingUp style={{ width: 15, height: 15 }} />اقترح لي خدمات تناسب وضعي
      </button>
      <div style={{ display: 'flex', gap: 8 }}>
        <button data-testid="restart-analysis-btn" onClick={onRestart}
          style={{ flex: 1, padding: 10, backgroundColor: 'white', color: C.muted, borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          <RotateCcw style={{ width: 13, height: 13 }} />إعادة التحليل
        </button>
        <button data-testid="go-home-btn" onClick={onGoHome}
          style={{ flex: 1, padding: 10, backgroundColor: 'white', color: C.muted, borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
          إنهاء
        </button>
      </div>
    </div>
  );

  // ── MAIN LEFT CONTENT ──
  const MainContent = () => (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── 1. HERO ── */}
      <div data-testid="result-hero" style={{
        background: `linear-gradient(145deg, ${st.bg}, white 70%)`,
        borderRadius: 18, padding: isMobile ? '20px 16px' : '22px 28px',
        border: `1px solid ${st.border}`,
        display: 'flex', alignItems: 'center', gap: isMobile ? 14 : 24,
        flexDirection: isMobile ? 'column' : 'row',
        textAlign: isMobile ? 'center' : 'right',
      }}>
        <ScoreGauge score={total_score} category={category} size={isMobile ? 110 : 120} />
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: C.secondary, margin: '0 0 4px', fontFamily: FONT }}>تحليل وضعك المالي</h2>
          {clientName && <p style={{ fontSize: 12, color: C.muted, margin: '0 0 6px', fontFamily: FONT }}>مرحباً {clientName}</p>}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: st.color, color: 'white', padding: '4px 14px', borderRadius: 9999, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
            <Icon style={{ width: 13, height: 13 }} />{category_label}
          </div>
          {insights?.insight_text && (
            <p style={{ fontSize: 13, color: C.text, margin: 0, lineHeight: 1.6, fontFamily: FONT, opacity: 0.85 }}>{insights.insight_text}</p>
          )}
        </div>
      </div>

      {/* ── 2. QUICK SUMMARY ── */}
      <div data-testid="result-quick-summary" style={{ display: 'flex', gap: 10, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <QuickCard label="الدخل الكلي" value={`${summary.total_income.toLocaleString()} ر`} icon={DollarSign} accent={C.green} />
        <QuickCard label="المصروفات" value={`${summary.total_expenses.toLocaleString()} ر`} icon={CreditCard} accent={C.orange} />
        <QuickCard label="الديون" value={summary.has_debt ? `${summary.total_debt.toLocaleString()} ر` : 'لا يوجد'} icon={AlertTriangle} accent={C.red} />
        <QuickCard label="المتبقي" value={`${disposable.toLocaleString()} ر`} icon={PiggyBank} accent={disposable >= 0 ? C.primary : C.red} />
      </div>

      {/* ── 3. RECOMMENDATION ── */}
      <div data-testid="result-recommendation" style={{ background: C.secondary, borderRadius: 14, padding: '16px 18px', color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Target style={{ width: 16, height: 16, color: C.gold }} />
          <span style={{ fontSize: 13, fontWeight: 700 }}>التوصية</span>
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.7, margin: 0, opacity: 0.95, fontFamily: FONT }}>{recommendation.message}</p>
        {recommendation.recommended_savings > 0 && recommendation.gap > 0 && (
          <p style={{ fontSize: 11, color: C.gold, margin: '6px 0 0', fontFamily: FONT }}>
            المستهدف: {recommendation.recommended_savings.toLocaleString()} ريال/شهرياً ({recommendation.savings_percentage}% من دخلك)
          </p>
        )}
      </div>

      {/* ── MOBILE: show sticky panel inline here ── */}
      {isMobile && <StickyPanel />}

      {/* ── 4. DETAILS (scrollable area) ── */}
      {/* Components Breakdown */}
      <div data-testid="result-components" style={{ background: 'white', borderRadius: 14, padding: 16, border: `1px solid ${C.border}` }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: C.secondary, marginBottom: 10, fontFamily: FONT }}>تفاصيل التقييم</h3>
        {Object.entries(components).map(([key, val]) => {
          const labels = { behavior: 'السلوك المالي', savings: 'الادخار', expenses: 'إدارة المصروفات', debt: 'إدارة الديون' };
          const max = components_max?.[key] || 25;
          if (max === 0) return null;
          const pct = (val / max) * 100;
          const barColor = pct >= 70 ? C.green : pct >= 40 ? C.orange : C.red;
          return (
            <div key={key} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{labels[key]}</span>
                <span style={{ fontSize: 11, color: C.muted }}>{val}/{max}</span>
              </div>
              <div style={{ height: 5, backgroundColor: C.bgGray, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, backgroundColor: barColor, borderRadius: 3, transition: 'width 1s ease-out' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Strength & Weakness */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ backgroundColor: C.greenBg, borderRadius: 12, padding: 12, border: `1px solid ${C.greenBorder}` }}>
          <span style={{ fontSize: 11, color: C.green, fontWeight: 700, display: 'block', marginBottom: 3 }}>نقطة قوة</span>
          <span style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{insights?.strength_point}</span>
        </div>
        <div style={{ backgroundColor: C.orangeBg, borderRadius: 12, padding: 12, border: `1px solid ${C.orangeBorder}` }}>
          <span style={{ fontSize: 11, color: C.orange, fontWeight: 700, display: 'block', marginBottom: 3 }}>نقطة تحسين</span>
          <span style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{insights?.weakness_point}</span>
        </div>
      </div>

      {/* Tips */}
      {insights?.tips?.length > 0 && (
        <div style={{ background: 'white', borderRadius: 14, padding: 16, border: `1px solid ${C.border}` }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: C.secondary, marginBottom: 8, fontFamily: FONT }}>نصائح لك</h3>
          {insights.tips.map((tip, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
              <ChevronLeft style={{ width: 13, height: 13, color: C.primary, marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{tip}</span>
            </div>
          ))}
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ backgroundColor: '#FEF9C3', borderRadius: 10, padding: 10, display: 'flex', gap: 8, alignItems: 'flex-start', border: '1px solid #FDE68A' }}>
        <AlertTriangle style={{ width: 14, height: 14, color: '#CA8A04', flexShrink: 0, marginTop: 1 }} />
        <span style={{ fontSize: 11, color: '#92400E', lineHeight: 1.5 }}>{disclaimer}</span>
      </div>
      <p style={{ textAlign: 'center', fontSize: 11, color: C.light, margin: 0 }}>يمكنك الرجوع لتحليلك لاحقاً باستخدام رقم الجوال</p>
    </div>
  );

  // ── LAYOUT ──
  return (
    <div data-testid="result-dashboard" style={{
      display: 'flex', gap: 20, fontFamily: FONT, direction: 'rtl',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'flex-start',
    }}>
      <MainContent />
      {!isMobile && <StickyPanel />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROGRESS SIDEBAR
// ═══════════════════════════════════════════════════════════════════════════════

function ProgressSidebar({ progress, currentStep }) {
  const steps = [
    { key: 'income', label: 'الدخل' },
    { key: 'behavior', label: 'السلوك المالي' },
    { key: 'expenses', label: 'المصروفات' },
    { key: 'debt', label: 'الديون والادخار' },
    { key: 'savings', label: 'الادخار' },
    { key: 'client_info', label: 'بياناتك' },
  ];
  const stepOrder = steps.map(s => s.key);
  const currentIdx = stepOrder.indexOf(currentStep);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: C.bgGray, fontFamily: FONT }}>
      <div style={{ padding: 20, borderBottom: `1px solid ${C.border}`, backgroundColor: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: C.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BarChart3 style={{ width: 20, height: 20, color: 'white' }} />
          </div>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 'bold', color: C.secondary, margin: 0 }}>التحليل المالي</h2>
            <p style={{ fontSize: 12, color: C.muted, margin: '2px 0 0' }}>{progress.percentage}% مكتمل</p>
          </div>
        </div>
        <div style={{ marginTop: 12, height: 6, backgroundColor: C.bgGray, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress.percentage}%`, backgroundColor: C.primary, borderRadius: 3, transition: 'width 0.5s' }} />
        </div>
      </div>
      <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
        {steps.map((step, idx) => {
          const done = idx < currentIdx;
          const curr = step.key === currentStep;
          const StIcon = STEP_ICONS[step.key] || BarChart3;
          return (
            <div key={step.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: done ? C.primary : curr ? C.secondary : 'white',
                  color: done || curr ? 'white' : C.light,
                  border: done || curr ? 'none' : `1px solid ${C.border}`, transition: 'all 0.3s' }}>
                  {done ? <CheckCircle style={{ width: 16, height: 16 }} /> : <StIcon style={{ width: 16, height: 16 }} />}
                </div>
                {idx < steps.length - 1 && <div style={{ width: 2, height: 28, marginTop: 4, backgroundColor: done ? C.primary : C.border }} />}
              </div>
              <div style={{ paddingTop: 6 }}>
                <h4 style={{ fontSize: 13, fontWeight: 'bold', color: done || curr ? C.secondary : C.light, margin: 0 }}>{step.label}</h4>
                {curr && <span style={{ fontSize: 11, color: C.gold, fontWeight: 600 }}>جارٍ الآن</span>}
                {done && <span style={{ fontSize: 11, color: C.primary, fontWeight: 500 }}>مكتمل</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// RETRIEVAL MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function RetrievalModal({ onClose, onViewResult }) {
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!mobile.trim()) return;
    setLoading(true); setError(""); setResults(null);
    try {
      const res = await axios.post(`${API}/financial/retrieve`, { mobile: mobile.trim() });
      if (res.data.found) {
        setResults(res.data.sessions);
      } else {
        setError("لم يتم العثور على تحليلات مرتبطة بهذا الرقم");
      }
    } catch {
      setError("حدث خطأ، حاول مرة أخرى");
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', borderRadius: 20, padding: 24, width: '90%', maxWidth: 420, maxHeight: '80vh', overflowY: 'auto', fontFamily: FONT }} dir="rtl">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 'bold', color: C.secondary, margin: 0 }}>استرجاع تحليلي السابق</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X style={{ width: 20, height: 20, color: C.muted }} /></button>
        </div>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input type="tel" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="أدخل رقم جوالك (05XXXXXXXX)"
            data-testid="retrieve-mobile-input"
            style={{ flex: 1, padding: '10px 14px', border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 14, fontFamily: FONT, outline: 'none', direction: 'ltr', textAlign: 'right' }} />
          <button type="submit" disabled={loading} data-testid="retrieve-search-btn"
            style={{ padding: '10px 16px', backgroundColor: C.primary, color: 'white', borderRadius: 10, border: 'none', cursor: 'pointer' }}>
            <Search style={{ width: 18, height: 18 }} />
          </button>
        </form>
        {error && <p style={{ fontSize: 13, color: C.red, textAlign: 'center' }}>{error}</p>}
        {results && results.map(r => {
          const catSt = CAT[r.category] || CAT.orange;
          return (
            <div key={r.session_id} onClick={() => onViewResult(r.session_id)}
              style={{ backgroundColor: C.bgGray, borderRadius: 12, padding: 14, marginBottom: 10, cursor: 'pointer', border: `1px solid ${C.border}`, transition: 'all 0.2s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 'bold', color: C.secondary }}>{r.client_name || 'تحليل'}</span>
                  <span style={{ display: 'block', fontSize: 11, color: C.muted }}>{new Date(r.completed_at).toLocaleDateString('ar-SA')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 20, fontWeight: 'bold', color: catSt.color }}>{r.score}</span>
                  <span style={{ fontSize: 10, backgroundColor: catSt.bg, color: catSt.color, padding: '2px 8px', borderRadius: 9999, border: `1px solid ${catSt.border}` }}>{r.category_label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function FinancialAdvisorPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("intro"); // intro | chat | result
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [progress, setProgress] = useState({ answered: 0, total: 13, percentage: 0 });
  const [currentStep, setCurrentStep] = useState("income");
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [result, setResult] = useState(null);
  const [insights, setInsights] = useState(null);
  const [disclaimer, setDisclaimer] = useState("");
  const [clientName, setClientName] = useState("");
  const [validationBlock, setValidationBlock] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [showRetrieval, setShowRetrieval] = useState(false);
  const [showSidebar, setShowSidebar] = useState(window.innerWidth >= 768);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, isTyping, scrollToBottom]);
  useEffect(() => {
    const h = () => setShowSidebar(window.innerWidth >= 768);
    window.addEventListener('resize', h); return () => window.removeEventListener('resize', h);
  }, []);

  const addMsg = useCallback((sender, text) => {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), sender, text }]);
  }, []);

  const startAnalysis = async () => {
    setPhase("chat"); setIsTyping(true); setValidationBlock(null); setValidationError("");
    try {
      const res = await axios.post(`${API}/financial/sessions`);
      const { session_id, question, progress: p } = res.data;
      setSessionId(session_id); setProgress(p);
      addMsg("ai", "خليني أساعدك تفهم وضعك المالي بشكل بسيط\nبجاوب على كم سؤال سريع وبعدها أعطيك تحليل واضح");
      setTimeout(() => {
        setIsTyping(false); setCurrentQuestion(question);
        setCurrentStep(question.step);
        addMsg("ai", question.text);
      }, 800);
    } catch {
      setIsTyping(false); addMsg("ai", "عذراً، حدث خطأ. حاول مرة أخرى.");
    }
  };

  const submitAnswer = async (answerValue, confirmed = false) => {
    if (!sessionId || (!currentQuestion && !validationBlock)) return;

    // If confirming after Rule 10 (suspicious low values — only confirmable case)
    if (validationBlock && confirmed) {
      setValidationBlock(null);
      addMsg("user", "نعم، المعلومات صحيحة");
      setIsTyping(true);
      try {
        const res = await axios.post(`${API}/financial/sessions/${sessionId}/answer`, {
          answer: "", confirmed: true
        });
        await new Promise(r => setTimeout(r, 600));
        setIsTyping(false);
        handleResponse(res.data);
      } catch {
        setIsTyping(false); addMsg("ai", "عذراً، حدث خطأ.");
      }
      return;
    }

    // If user clicks "تعديل البيانات" after a block
    if (validationBlock && !confirmed) {
      setValidationBlock(null);
      // The backend already reset the fields, just show the returned question
      return;
    }

    // Regular answer
    const displayValue = currentQuestion?.options?.find(o => o.value === answerValue)?.label
      || (currentQuestion?.suffix ? `${answerValue} ${currentQuestion.suffix}` : answerValue);

    addMsg("user", displayValue);
    setCurrentQuestion(null); setInputValue(""); setIsTyping(true);
    setValidationError("");

    try {
      const res = await axios.post(`${API}/financial/sessions/${sessionId}/answer`, { answer: answerValue, confirmed });
      await new Promise(r => setTimeout(r, 600));
      setIsTyping(false);
      handleResponse(res.data);
    } catch {
      setIsTyping(false); addMsg("ai", "عذراً، حدث خطأ. حاول مرة أخرى.");
    }
  };

  const handleResponse = (data) => {
    if (data.validation_error) {
      setValidationError(data.validation_error);
      setCurrentQuestion(data.question);
      setCurrentStep(data.question?.step || currentStep);
      addMsg("ai", data.validation_error);
      return;
    }

    // HARD BLOCK — validation failed
    if (data.validation_block) {
      const block = data.validation_block;
      addMsg("ai", block.message);
      setProgress(data.progress);

      if (data.needs_confirmation && block.allow_confirmation) {
        // Rule 10 only — show confirmation buttons
        setValidationBlock(block);
        setCurrentQuestion(null);
      } else {
        // Hard block — show "تعديل البيانات" and the next question to fix
        setValidationBlock(null);
        if (data.question) {
          setCurrentQuestion(data.question);
          setCurrentStep(data.question.step);
        }
      }
      return;
    }

    if (data.completed) {
      setResult(data.score_result);
      setInsights(data.insights);
      setDisclaimer(data.disclaimer);
      setClientName(data.client_name || "");
      setProgress(data.progress);
      addMsg("ai", "تم التحليل! دعني أعرض لك النتائج...");
      setTimeout(() => setPhase("result"), 1200);
    } else {
      const { question, progress: p } = data;
      setProgress(p); setCurrentStep(question.step);
      setCurrentQuestion(question);
      addMsg("ai", question.text);
    }
  };

  const handleNumberSubmit = (e) => {
    e.preventDefault();
    const v = inputValue.trim();
    if (!v || isNaN(v) || Number(v) < 0) return;
    submitAnswer(v);
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    const v = inputValue.trim();
    if (!v) return;
    submitAnswer(v);
  };

  const handleRestart = async () => {
    if (sessionId) {
      try {
        const res = await axios.post(`${API}/financial/sessions/${sessionId}/reset`);
        setMessages([]); setResult(null); setInsights(null); setClientName("");
        setValidationBlock(null); setValidationError("");
        setPhase("chat"); setIsTyping(true);
        const { question, progress: p } = res.data;
        setProgress(p);
        addMsg("ai", "يلا نبدأ من جديد!");
        setTimeout(() => {
          setIsTyping(false); setCurrentQuestion(question);
          setCurrentStep(question.step); addMsg("ai", question.text);
        }, 800);
      } catch { setPhase("intro"); setMessages([]); }
    } else { setPhase("intro"); setMessages([]); }
  };

  const handleSuggestServices = async () => {
    if (sessionId) {
      try { await axios.post(`${API}/financial/sessions/${sessionId}/convert`); } catch {}
    }
    navigate("/");
  };

  const handleViewRetrievedResult = async (sid) => {
    setShowRetrieval(false);
    try {
      const res = await axios.get(`${API}/financial/retrieve/${sid}`);
      const d = res.data;
      setResult(d.score_result); setInsights(d.insights);
      setDisclaimer(d.disclaimer); setClientName(d.client_name || "");
      setSessionId(sid); setPhase("result");
      addMsg("ai", "هذا تحليلك السابق:");
    } catch { addMsg("ai", "عذراً، لم نتمكن من استرجاع التحليل."); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div style={{ height: '100vh', width: '100%', display: 'flex', overflow: 'hidden', backgroundColor: C.bgGray, fontFamily: FONT }}>
      {showRetrieval && <RetrievalModal onClose={() => setShowRetrieval(false)} onViewResult={handleViewRetrievedResult} />}

      {/* Chat Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: phase === "result" ? C.bgGray : 'white', minWidth: 0 }} dir="rtl">
        {/* Header */}
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: phase === "result" ? '10px 20px' : '16px 20px', borderBottom: `1px solid ${C.border}`, backgroundColor: 'white', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <HalaAvatar state={isTyping ? AVATAR_STATES.THINKING : AVATAR_STATES.IDLE} size={phase === "result" ? 36 : 48} showStatusDot />
            <div>
              <h1 style={{ fontSize: 14, fontWeight: 'bold', color: C.secondary, margin: 0, fontFamily: FONT }}>هلا مستشارتك المالية</h1>
              {phase !== "result" && <p style={{ fontSize: 11, color: C.muted, margin: 0, fontFamily: FONT }}>{isTyping ? 'جاري التحليل...' : 'تحليل وضعك المالي'}</p>}
            </div>
          </div>
          <button data-testid="back-to-home-btn" onClick={() => navigate("/")}
            style={{ fontSize: 12, color: C.muted, border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: 9999, backgroundColor: 'white', cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft style={{ width: 14, height: 14 }} />الرئيسية
          </button>
        </header>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: phase === "result" ? '16px 20px' : '24px 20px' }}>
          {/* INTRO */}
          {phase === "intro" && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', padding: '0 16px' }}>
              <HalaAvatar state={AVATAR_STATES.IDLE} size={100} showStatusDot={false} />
              <div style={{ width: 60, height: 60, borderRadius: '50%', backgroundColor: C.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px auto' }}>
                <BarChart3 style={{ width: 30, height: 30, color: C.gold }} />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 'bold', color: C.secondary, marginBottom: 12, fontFamily: FONT }}>هلا مستشارتك المالية</h2>
              <p style={{ color: C.muted, fontSize: 14, marginBottom: 32, maxWidth: 380, lineHeight: 1.8, fontFamily: FONT }}>
                خليني أساعدك تفهم وضعك المالي بشكل بسيط، بجاوب على كم سؤال سريع وبعدها أعطيك تحليل واضح
              </p>
              <button data-testid="start-analysis-btn" onClick={startAnalysis}
                style={{ padding: '16px 48px', backgroundColor: C.primary, color: 'white', borderRadius: 12, fontWeight: 'bold', fontSize: 16, border: 'none', cursor: 'pointer', fontFamily: FONT, boxShadow: '0 10px 15px -3px rgba(29,143,78,0.2)', marginBottom: 12 }}>
                ابدأ التحليل
              </button>
              <button data-testid="retrieve-btn" onClick={() => setShowRetrieval(true)}
                style={{ padding: '12px 32px', backgroundColor: 'transparent', color: C.secondary, borderRadius: 12, fontWeight: 600, fontSize: 14, border: `1px solid ${C.border}`, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Search style={{ width: 16, height: 16 }} />استرجاع تحليلي السابق
              </button>
            </div>
          )}

          {/* CHAT */}
          {phase === "chat" && (
            <>
              {messages.map(msg => msg.sender === "ai" ? <AiBubble key={msg.id} text={msg.text} /> : <UserBubble key={msg.id} text={msg.text} />)}
              {isTyping && <TypingDots />}
              <div ref={messagesEndRef} />
            </>
          )}

          {/* RESULT — Full dashboard, NO chat messages */}
          {phase === "result" && result && (
            <ResultView result={result} insights={insights} disclaimer={disclaimer} clientName={clientName}
              onRestart={handleRestart} onSuggestServices={handleSuggestServices} onGoHome={() => navigate("/")} />
          )}
        </div>

        {/* Rule 10 confirmation buttons (suspicious low values — only confirmable case) */}
        {phase === "chat" && validationBlock && !isTyping && (
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, backgroundColor: 'rgba(254,243,243,0.3)', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button data-testid="confirm-values-btn" onClick={() => submitAnswer(null, true)}
                style={{ flex: 1, padding: 10, backgroundColor: C.primary, color: 'white', borderRadius: 9999, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                نعم، المعلومات صحيحة
              </button>
              <button data-testid="review-values-btn" onClick={() => submitAnswer(null, false)}
                style={{ flex: 1, padding: 10, backgroundColor: 'white', color: C.secondary, borderRadius: 9999, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                تعديل البيانات
              </button>
            </div>
          </div>
        )}

        {/* Choice / Region buttons */}
        {phase === "chat" && currentQuestion && !isTyping && !validationBlock && (currentQuestion.type === "choice" || currentQuestion.type === "scored_choice") && (
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, backgroundColor: 'rgba(249,250,251,0.5)', flexShrink: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {currentQuestion.options.map(opt => (
                <button key={opt.value} data-testid={`option-${opt.value}`} onClick={() => submitAnswer(opt.value)}
                  style={{ padding: '10px 18px', backgroundColor: 'white', border: `1px solid ${C.border}`, borderRadius: 9999, fontSize: 14, color: C.secondary, fontWeight: 500, cursor: 'pointer', fontFamily: FONT, transition: 'all 0.2s' }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Region choice */}
        {phase === "chat" && currentQuestion && !isTyping && !validationBlock && currentQuestion.type === "region_choice" && (
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, backgroundColor: 'rgba(249,250,251,0.5)', flexShrink: 0, maxHeight: 200, overflowY: 'auto' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {currentQuestion.options.map(opt => (
                <button key={opt.value} data-testid={`region-${opt.value}`} onClick={() => submitAnswer(opt.value)}
                  style={{ padding: '8px 14px', backgroundColor: 'white', border: `1px solid ${C.border}`, borderRadius: 9999, fontSize: 13, color: C.secondary, fontWeight: 500, cursor: 'pointer', fontFamily: FONT }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Number input */}
        {phase === "chat" && currentQuestion && !isTyping && !validationBlock && currentQuestion.type === "number" && (
          <div style={{ padding: '16px 20px', borderTop: `1px solid ${C.border}`, backgroundColor: 'white', flexShrink: 0 }}>
            {validationError && <p style={{ fontSize: 12, color: C.red, margin: '0 0 8px', fontFamily: FONT }}>{validationError}</p>}
            <form onSubmit={handleNumberSubmit} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="number" min="0" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={currentQuestion.placeholder || "أدخل المبلغ"}
                data-testid="number-input" autoFocus
                style={{ flex: 1, padding: '12px 16px', backgroundColor: C.bgGray, border: `1px solid ${validationError ? C.red : C.border}`, borderRadius: 12, fontSize: 14, outline: 'none', fontFamily: FONT, direction: 'rtl' }} />
              {currentQuestion.suffix && <span style={{ fontSize: 13, color: C.muted }}>{currentQuestion.suffix}</span>}
              <button type="submit" disabled={!inputValue.trim()} data-testid="submit-number-btn"
                style={{ width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: inputValue.trim() ? C.primary : C.bgGray, border: 'none', cursor: inputValue.trim() ? 'pointer' : 'default' }}>
                <Send style={{ width: 20, height: 20, color: inputValue.trim() ? 'white' : C.light, transform: 'rotate(180deg)' }} />
              </button>
            </form>
          </div>
        )}

        {/* Text input (name) */}
        {phase === "chat" && currentQuestion && !isTyping && !validationBlock && (currentQuestion.type === "text" || currentQuestion.type === "phone") && (
          <div style={{ padding: '16px 20px', borderTop: `1px solid ${C.border}`, backgroundColor: 'white', flexShrink: 0 }}>
            {validationError && <p style={{ fontSize: 12, color: C.red, margin: '0 0 8px', fontFamily: FONT }}>{validationError}</p>}
            <form onSubmit={handleTextSubmit} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type={currentQuestion.type === "phone" ? "tel" : "text"}
                value={inputValue} onChange={e => setInputValue(e.target.value)}
                placeholder={currentQuestion.placeholder || ""}
                data-testid={currentQuestion.type === "phone" ? "phone-input" : "text-input"}
                autoFocus
                style={{ flex: 1, padding: '12px 16px', backgroundColor: C.bgGray, border: `1px solid ${validationError ? C.red : C.border}`, borderRadius: 12, fontSize: 14, outline: 'none', fontFamily: FONT, direction: currentQuestion.type === "phone" ? 'ltr' : 'rtl', textAlign: currentQuestion.type === "phone" ? 'left' : 'right' }} />
              <button type="submit" disabled={!inputValue.trim()} data-testid="submit-text-btn"
                style={{ width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: inputValue.trim() ? C.primary : C.bgGray, border: 'none', cursor: inputValue.trim() ? 'pointer' : 'default' }}>
                <Send style={{ width: 20, height: 20, color: inputValue.trim() ? 'white' : C.light, transform: 'rotate(180deg)' }} />
              </button>
            </form>
          </div>
        )}

        {/* Footer — hidden in result phase */}
        {phase !== "result" && (
        <div style={{ padding: '8px 0', textAlign: 'center', borderTop: `1px solid ${C.border}`, backgroundColor: 'rgba(249,250,251,0.3)', flexShrink: 0 }}>
          <p style={{ fontSize: 11, color: C.light, margin: 0, fontFamily: FONT }}>هلا مستشارتك المالية</p>
        </div>
        )}
      </div>

      {/* Sidebar — only during chat phase, hidden in result (ResultView has its own panel) */}
      {showSidebar && phase === "chat" && (
        <div style={{ width: 340, flexShrink: 0, borderRight: `1px solid ${C.border}`, height: '100%', overflow: 'hidden' }} dir="rtl">
          <ProgressSidebar progress={progress} currentStep={currentStep} />
        </div>
      )}

      <style>{`
        @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        .msg-enter { animation: fadeUp 0.3s ease-out; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
